import React from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Button, Modal, Table, Tag } from 'antd';

import { ReviewPart, ReviewStatus, ReviewVariant } from '../../utils/reviewSummary';

interface Props {
    parts: Array<ReviewPart>;
    open: boolean;
    onClose: () => void;
    onEditPart: (masterProductId: number, lineItemId: number) => void;
}

const STATUS_COLORS: Record<ReviewStatus, string> = {
    ready: 'green',
    'missing-specs': 'red',
    'analysis-pending': 'red',
    'manual-quote': 'gold',
};

interface ReviewRow {
    key: string;
    partNumber: number;
    partName: string;
    partRowSpan: number;
    isFirstVariant: boolean;
    masterProductId: number;
    variant: ReviewVariant;
}

const SelectedSpec = ({
    variant,
    label,
}: {
    variant: ReviewVariant;
    label: 'Material' | 'Finish' | 'Coating';
}) => {
    const selected = variant.specs.find(spec => spec.label === label)?.selected;
    return (
        <span
            className={selected ? undefined : 'review-summary-missing'}
            data-testid={`quote-review-summary-${label.toLowerCase()}`}
            data-line-item-id={variant.lineItemId}
            data-selected={selected ? 'true' : 'false'}
        >
            {selected ?? 'Not selected'}
        </span>
    );
};

export const ReviewSummary = ({ parts, open, onClose, onEditPart }: Props) => {
    const rows: Array<ReviewRow> = parts.flatMap((part, partIndex) =>
        part.variants.map((variant, variantIndex) => ({
            key: `${part.masterProductId}-${variant.id}`,
            partNumber: partIndex + 1,
            partName: part.name,
            partRowSpan: part.variants.length,
            isFirstVariant: variantIndex === 0,
            masterProductId: part.masterProductId,
            variant,
        }))
    );

    const columns: Array<any> = [
        {
            title: 'Part',
            key: 'part',
            width: 220,
            render: (_value: unknown, row: ReviewRow) => ({
                children: row.isFirstVariant ? (
                    <div
                        data-testid="quote-review-summary-part"
                        data-master-product-id={row.masterProductId}
                        data-part-number={row.partNumber}
                    >
                        <strong>Part {row.partNumber}</strong>
                        <div className="review-summary-part-name">{row.partName}</div>
                    </div>
                ) : null,
                props: { rowSpan: row.isFirstVariant ? row.partRowSpan : 0 },
            }),
        },
        {
            title: 'Qty',
            key: 'quantity',
            width: 100,
            // The Part cell exists only for a part's first variant, so the row identity lives in this cell.
            render: (_value: unknown, row: ReviewRow) => (
                <div
                    data-testid="quote-review-summary-row"
                    data-master-product-id={row.masterProductId}
                    data-line-item-id={row.variant.lineItemId}
                    data-part-number={row.partNumber}
                    data-status={row.variant.status}
                >
                    <span data-testid="quote-review-summary-quantities">{row.variant.quantities.join(', ')}</span>
                </div>
            ),
        },
        {
            title: 'Material',
            key: 'material',
            width: 200,
            render: (_value: unknown, row: ReviewRow) => <SelectedSpec label="Material" variant={row.variant} />,
        },
        {
            title: 'Finish',
            key: 'finish',
            width: 200,
            render: (_value: unknown, row: ReviewRow) => <SelectedSpec label="Finish" variant={row.variant} />,
        },
        {
            title: 'Coating',
            key: 'coating',
            width: 200,
            render: (_value: unknown, row: ReviewRow) => <SelectedSpec label="Coating" variant={row.variant} />,
        },
        {
            title: 'Status',
            key: 'status',
            width: 150,
            render: (_value: unknown, row: ReviewRow) => (
                <Tag
                    color={STATUS_COLORS[row.variant.status]}
                    data-testid="quote-review-summary-status"
                    data-line-item-id={row.variant.lineItemId}
                    data-status={row.variant.status}
                >
                    {row.variant.statusLabel}
                </Tag>
            ),
        },
        {
            title: '',
            key: 'actions',
            width: 90,
            render: (_value: unknown, row: ReviewRow) => (
                <Button
                    aria-label={`Edit ${row.partName}`}
                    data-testid="quote-review-summary-edit"
                    data-line-item-id={row.variant.lineItemId}
                    data-master-product-id={row.masterProductId}
                    icon={<EditOutlined />}
                    onClick={() => {
                        onClose();
                        onEditPart(row.masterProductId, row.variant.lineItemId);
                    }}
                    size="small"
                >
                    Edit
                </Button>
            ),
        },
    ];

    return (
        <Modal
            className="review-summary-modal"
            footer={null}
            onCancel={onClose}
            open={open}
            style={{ top: 16 }}
            title="Review summary"
            width="min(1280px, calc(100vw - 32px))"
        >
            <div data-testid="quote-review-summary-modal">
                <p className="review-summary-modal-intro">
                    Review the current selections for every quoted part.
                </p>
                <Table
                    columns={columns}
                    dataSource={rows}
                    pagination={false}
                    rowKey="key"
                    rowClassName={row => (row.partNumber % 2 === 0 ? 'review-summary-row-alternate' : '')}
                    scroll={{ x: 960 }}
                    size="small"
                />
            </div>
        </Modal>
    );
};

export default ReviewSummary;
